import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { createUseStyles, useTheme, Text, Button } from '@Sage/hoi-poi-ui';

import LottieGif from 'components/LottieGif';

import styles from './styles.js';

const useStyles = createUseStyles(styles, 'CenteredActionScreen');

const ILLUSTRATION_SIZE = 120;

const actionShape = PropTypes.shape({
    label: PropTypes.string.isRequired,
    icon: PropTypes.string,
    iconPosition: PropTypes.oneOf(['left', 'right']),
    onClick: PropTypes.func.isRequired,
    type: PropTypes.string,
    isLoading: PropTypes.bool,
});

const CenteredActionScreen = ({
    title,
    body,
    illustration,
    illustrationNode,
    useIllustrationBackdrop = true,
    actions,
    footer,
}) => {
    const classes = useStyles();
    const theme = useTheme();

    const hasActions = Array.isArray(actions) && actions.length > 0;

    return (
        <div className={classes.centerContent}>
            <Text type="h5" medium>
                {title}
            </Text>
            <div
                className={[
                    classes.illustration,
                    !useIllustrationBackdrop ? classes.illustrationNoBackdrop : null,
                ]
                    .filter(Boolean)
                    .join(' ')}
            >
                {illustrationNode ??
                    (illustration ? (
                        <LottieGif
                            src={illustration}
                            width={ILLUSTRATION_SIZE}
                            height={ILLUSTRATION_SIZE}
                        />
                    ) : null)}
            </div>
            {body && (
                <Text type="body" color={theme.colors.textLight.secondary} className={classes.body}>
                    {body}
                </Text>
            )}
            {footer != null ? (
                <div className={classes.footer}>{footer}</div>
            ) : (
                hasActions && (
                    <div className={classes.footer}>
                        <div className={classes.actionsRow}>
                            {actions.map((action) => (
                                <Button
                                    key={action.label}
                                    type={action.type ?? 'primary'}
                                    size="big"
                                    onClick={action.onClick}
                                    isLoading={action.isLoading}
                                    icon={action.icon}
                                    iconPosition={action.iconPosition ?? 'left'}
                                >
                                    {action.label}
                                </Button>
                            ))}
                        </div>
                    </div>
                )
            )}
        </div>
    );
};

CenteredActionScreen.propTypes = {
    title: PropTypes.string.isRequired,
    body: PropTypes.string,
    /** Lottie animation JSON (imported object or string) passed to LottieGif `src`. */
    illustration: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    /** Optional React node to render instead of Lottie `illustration`. */
    illustrationNode: PropTypes.node,
    /** Controls the circular black backdrop behind the illustration area. */
    useIllustrationBackdrop: PropTypes.bool,
    actions: PropTypes.arrayOf(actionShape),
    /** Replaces the default action button area (e.g. sync stepper). */
    footer: PropTypes.node,
};

CenteredActionScreen.displayName = 'CenteredActionScreen';

export default memo(CenteredActionScreen);
